import { JSX, ReactNode } from 'react';
import { Table, Sheet, Checkbox } from '@mui/joy';
import { Pagination } from '../Pagination/Pagination';
import { Typography } from '@mui/joy';
import { Loader } from '../Loader/Loader';

type ColumnDef =
  | string
  | { key: string; label?: ReactNode; align?: 'left' | 'center' | 'right' };

type DataTableProps<T> = {
  data: T[];
  columns: ColumnDef[];
  isLoading?: boolean;
  children?: {
    [key: string]: ({ item }: { item: T }) => ReactNode;
  };
  renderEmpty?: () => ReactNode;
  page?: number;
  setPage?: React.Dispatch<React.SetStateAction<number>>;
  totalPages?: number;
  selectedKeys?: string[];
  onSelectChange?: (str: string[]) => void;
  getRowKey?: (item: T) => string;
  onRowClick?: (item: T) => void;
};

export function DataTable<T>({
  data,
  columns,
  isLoading = false,
  children = {},
  renderEmpty,
  page,
  setPage,
  totalPages,
  selectedKeys = [],
  onSelectChange,
  getRowKey = (item: T) => (item as any)?.id,
  onRowClick = () => {},
}: DataTableProps<T>): JSX.Element {
  const resolvedColumns = columns.map((col) =>
    typeof col === 'string' ? { key: col, label: col } : col
  );

  return (
    <>
      <Sheet
        variant="outlined"
        sx={{
          width: '100%',
          borderRadius: 'sm',
          overflow: 'auto',
          minHeight: 0,
        }}
      >
        {isLoading ? <Loader /> : null}
        <Table
          stickyHeader
          hoverRow
          sx={{
            '--TableCell-headBackground':
              'var(--joy-palette-background-level1)',
            '--Table-headerUnderlineThickness': '1px',
            '--TableRow-hoverBackground':
              'var(--joy-palette-background-level1)',
            '--TableCell-paddingY': '4px',
            '--TableCell-paddingX': '8px',
            tableLayout: 'auto',
          }}
        >
          <thead>
            <tr>
              {resolvedColumns.map((col) => (
                <th
                  key={col.key}
                  style={{
                    padding: '12px 6px',
                    textAlign:
                      col.key === '__select__' ? 'start' : col.align ?? 'left',
                  }}
                >
                  {col.key === '__select__' ? (
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                      }}
                    >
                      <Checkbox
                        size="sm"
                        indeterminate={
                          selectedKeys.length > 0 &&
                          selectedKeys.length !== data?.length
                        }
                        checked={
                          !!data?.length && selectedKeys.length === data?.length
                        }
                        onChange={(event) => {
                          onSelectChange?.(
                            event.target.checked ? data.map(getRowKey) : []
                          );
                        }}
                        color={
                          selectedKeys.length > 0 ||
                          selectedKeys.length === data.length
                            ? 'primary'
                            : undefined
                        }
                        sx={{ verticalAlign: 'text-bottom' }}
                      />
                    </div>
                  ) : (
                    col.label ?? col.key
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody
            style={{
              height: isLoading ? '300px' : 'auto',
            }}
          >
            {isLoading ? null : data?.length ? (
              data?.map((item, rowIndex) => {
                const rowKey = getRowKey(item);
                return (
                  <tr
                    key={rowKey || rowIndex}
                    onClick={(e) => {
                      e.stopPropagation();
                      onRowClick?.(item);
                    }}
                    style={{
                      cursor: 'pointer',
                    }}
                  >
                    {resolvedColumns.map((col) => (
                      <td
                        key={col.key}
                        style={{
                          padding: '4px 8px',
                          textAlign: col?.align,
                        }}
                      >
                        {col.key === '__select__' ? (
                          <Checkbox
                            size="sm"
                            checked={selectedKeys.includes(rowKey)}
                            onChange={(event) => {
                              const updatedKeys = event.target.checked
                                ? [...selectedKeys, rowKey]
                                : selectedKeys.filter((id) => id !== rowKey);
                              onSelectChange?.(updatedKeys);
                            }}
                            onClick={(e) => e.stopPropagation()}
                          />
                        ) : children[col.key] ? (
                          <Typography level="body-xs">
                            {children[col.key]!({ item })}
                          </Typography>
                        ) : (
                          <Typography level="body-xs">
                            {(item as any)[col.key]}
                          </Typography>
                        )}
                      </td>
                    ))}
                  </tr>
                );
              })
            ) : (
              <tr>
                <td
                  align="center"
                  colSpan={resolvedColumns.length}
                  className="text-center !p-4"
                >
                  {renderEmpty ? renderEmpty() : 'No records found'}
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </Sheet>

      {!!totalPages && !!page && !!setPage && (
        <Pagination page={page} totalPages={totalPages} setPage={setPage} />
      )}
    </>
  );
}
